import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  ListItem,
  OrderedList,
  Text,
  Select,
  Spinner,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { format, utcToZonedTime } from 'date-fns-tz';

import { EVENT_TITLE } from '../../../Constants/AppConstants';
import { EVENT_COMPETITION_WITH_SCORE_DETAILS } from '../../../GraphQL/Queries';
import useHeaderComponentsMutation from '../../../Hooks/useHeaderComponentsMutation';

import { Provider } from '../../../Chakra';
import { Link } from '../../../Chakra/components/Link';

const DISPLAY_DATE_FORMAT = 'MMM d yyyy hh:mm aa';
const USER_LONG_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const USER_SHORT_TZ = format(new Date(), 'z');
const stringifyDate = (date) => format(utcToZonedTime(date, USER_LONG_TZ), DISPLAY_DATE_FORMAT);

const FETCH_EVENT = gql`
  query fetchEventDetail($id: Int!) {
    fetchEventDetail(id: $id) {
      id
      title
      fetchEventCompetitions {
        id
        title
        exportBallots
        rounds {
          id
          name
          status
        }
        fetchCompetition {
          id
          title
          fetchCategory {
            id
            title
          }
        }
      }
    }
  }
`;

/**
 * EditBallotScoresInput = {
 *  eventCompetitionId: ...
 *  ballotScores: [
 *    { id: ..., comments: '', approved: false },
 *  ]
 * }
 */
const EDIT_BALLOT_SCORES = gql`
mutation editBallotScores($input: EditBallotScoresInput!) {
  editBallotScores(input: $input) {
    ballotScores {
      id
      totalScore
      judge { id fetchUser { firstname lastname email }}
      sections {
        id
        ballotSection { id }
        points
      }
      comments
      privateNotes
      status
    }
  }
}
`;

const MODIFY_EVENT_COMPETITION = gql`
mutation modifyEventCompetition($id: ID!, $exportBallots: Boolean!) {
  modifyEventCompetition(input: {
    id: $id,
    exportBallots: $exportBallots
  }) {
    eventCompetition {
      id
      title
      exportBallots
    }
  }
}
`;

export const BallotReview = () => (
  <Provider>
    <BallotReviewInner />
  </Provider>
);

const judgablesForRound = (round) => round.judgables.filter((j) => j.penalty.attendance);

const getFirstMember = (round) => {
  const firstJudgable = judgablesForRound(round)[0];
  if (firstJudgable.type === 'Member') {
    return firstJudgable;
  }
  return {
    ...firstJudgable.members[0],
    ballotScores: firstJudgable.ballotScores,
  };
};

const getLastMember = (round) => {
  const judgables = judgablesForRound(round);
  const lastJudgable = judgables[judgables.length - 1];
  if (lastJudgable.type === 'Member') {
    return lastJudgable;
  }
  return {
    ...lastJudgable.members[lastJudgable.members.length - 1],
    ballotScores: lastJudgable.ballotScores,
  };
};

const getAllMembers = (round) => judgablesForRound(round).flatMap((judgable) => {
  if (judgable.type === 'Member') {
    return [judgable];
  }
  return judgable.members.map((m) => ({
    ...m,
    ballotScores: judgable.ballotScores,
  }));
});

const buildLink = (
  eventId,
  eventCompetitionId,
  roundId,
  memberId,
) => {
  if (memberId) {
    return `/events/${eventId}/ballots/${eventCompetitionId}/rounds/${roundId}/competitors/${memberId}`;
  }
  return `/events/${eventId}/ballots/${eventCompetitionId}/rounds/${roundId}`;
};

const NavigationDirectionContext = createContext(null);

const BallotReviewInner = () => {
  const {
    eventId,
    eventCompetitionId,
    roundId,
  } = useParams();
  const [navigationDirection, setNavigationDirection] = useState(true);

  const {
    data,
    loading: eventLoading,
  } = useQuery(FETCH_EVENT, {
    fetchPolicy: 'no-cache',
    variables: { id: parseInt(eventId, 10) },
  });

  const event = data?.fetchEventDetail;

  useHeaderComponentsMutation({
    title: 'Review Ballots',
    backLink: `/event-manage/${eventId}`,
    components: [{ key: EVENT_TITLE, value: event?.title }],
  });

  if (eventLoading) {
    return (
      <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const sortedEventCompetitions = event.fetchEventCompetitions
    .map((ec) => ({
      ...ec,
      rounds: ec.rounds
        .filter((r) => r.status === 'finalized'),
    }))
    .filter((ec) => ec.rounds.length > 0)
    .sort((ec1, ec2) => {
      const c1 = ec1.fetchCompetition;
      const c2 = ec2.fetchCompetition;
      if (c1.id === c2.id) {
        return ec1.title.localeCompare(ec2.title);
      }
      if (c1.fetchCategory.id === c2.fetchCategory.id) {
        return c1.title.localeCompare(c2.title);
      }
      return c1.fetchCategory.id - c2.fetchCategory.id;
    });

  if (sortedEventCompetitions.length === 0) {
    return <CenteredMessage message="No finalized competitions available" />
  }

  if (!eventCompetitionId && !roundId) {
    const eventCompetition = sortedEventCompetitions[0];
    const round = eventCompetition.rounds[0];

    return <Redirect to={buildLink(eventId, eventCompetition.id, round.id)} />;
  }

  const currentEventCompetitionIdx = sortedEventCompetitions.findIndex((ec) => ec.id === eventCompetitionId);

  let prevEventCompetitionLink = null;
  if (currentEventCompetitionIdx - 1 >= 0) {
    const prevEventCompetition = sortedEventCompetitions[currentEventCompetitionIdx - 1];
    const prevRound = prevEventCompetition.rounds[prevEventCompetition.rounds.length - 1];
    prevEventCompetitionLink = buildLink(eventId, prevEventCompetition.id, prevRound.id);
  }
  let nextEventCompetitionLink = null;
  if (currentEventCompetitionIdx + 1 < sortedEventCompetitions.length) {
    const nextEventCompetition = sortedEventCompetitions[currentEventCompetitionIdx + 1];
    const nextRound = nextEventCompetition.rounds[0];
    nextEventCompetitionLink = buildLink(eventId, nextEventCompetition.id, nextRound.id);
  }

  return (
    <NavigationDirectionContext.Provider value={{ navigationDirection, setNavigationDirection }}>
      <Grid templateColumns="2fr 5fr">
        <GridItem>
          <CompetitionsList
            eventId={eventId}
            eventCompetitions={sortedEventCompetitions}
            selectedRoundId={roundId}
          />
        </GridItem>
        <GridItem>
          <BallotReviewContent
            prevCompetitionLink={prevEventCompetitionLink}
            nextCompetitionLink={nextEventCompetitionLink}
          />
        </GridItem>
      </Grid>
    </NavigationDirectionContext.Provider>
  );
};

const CompetitionsList = ({ eventId, eventCompetitions, selectedRoundId }) => {
  const byCategory = Object.groupBy(eventCompetitions, (eventCompetition) => eventCompetition.fetchCompetition.fetchCategory.title);

  return (
    Object.keys(byCategory).map((categoryTitle) => (
      <Category
        key={categoryTitle}
        eventId={eventId}
        categoryTitle={categoryTitle}
        eventCompetitions={byCategory[categoryTitle]}
        selectedRoundId={selectedRoundId}
      />
    ))
  );
};

const Category = ({ eventId, categoryTitle, eventCompetitions, selectedRoundId }) => {
  const initialExpanded = eventCompetitions.some(ec => ec.rounds.some(r => r.id === selectedRoundId));
  const [expanded, setExpanded] = useState(initialExpanded);
  const [prevSelectedRoundId, setPrevSelectedRoundId] = useState();
  if (selectedRoundId !== prevSelectedRoundId) {
    setPrevSelectedRoundId(selectedRoundId);
    if (eventCompetitions.some(ec => ec.rounds.some(r => r.id === selectedRoundId))) {
      setExpanded(true);
    }
  }

  return (
    <Box>
      <HStack justifyContent="flex-start">
        <IconButton
          variant="ghost"
          size="sm"
          icon={expanded ? <FaChevronDown /> : <FaChevronRight />}
          onClick={() => setExpanded(!expanded)}
          padding={0}
          margin={0}
        />
        <Text weight="bold" margin={0} padding={0}>{categoryTitle}</Text>
      </HStack>
      {expanded && (
        <OrderedList styleType="none" margin={0} padding={0}>
          {eventCompetitions.flatMap((eventCompetition) => (
            <CompetitionRows
              key={eventCompetition.id}
              eventId={eventId}
              eventCompetitionId={eventCompetition.id}
              selectedRoundId={selectedRoundId}
            />
          ))}
        </OrderedList>
      )}
    </Box>
  );
};

const CompetitionRows = ({ eventId, eventCompetitionId, selectedRoundId }) => {
  const { setNavigationDirection } = useContext(NavigationDirectionContext);
  const {
    data,
    loading: eventCompetitionLoading,
  } = useQuery(EVENT_COMPETITION_WITH_SCORE_DETAILS, {
    fetchPolicy: 'network-first',
    variables: { id: eventCompetitionId },
  });
  const [modifyEventCompetition, { loading: modifyLoading }] = useMutation(MODIFY_EVENT_COMPETITION);
  const onToggleCompetition = async (eventCompId, isEnabled) => {
    modifyEventCompetition({
      variables: {
        id: eventCompId,
        exportBallots: isEnabled,
      },
    });
  };

  const loading = eventCompetitionLoading || modifyLoading;
  const eventCompetition = data?.fetchEventCompetitionDetail;

  if (eventCompetitionLoading) {
    return null;
  }

  return eventCompetition.rounds.map((round) => {
    const label = eventCompetition.rounds.length > 1 ? round.name : eventCompetition.title;

    const allMembers = getAllMembers(round);
    const allBallots = allMembers?.flatMap((m) => m.ballotScores);
    const approvedBallots = allBallots.filter((b) => b.status === 'approved');
    const completed = approvedBallots.length === allBallots.length;

    return (
      <ListItem key={round.id} marginBlockEnd={2}>
        <HStack justifyContent="flex-start" alignItems="center" paddingInline={2}>
          {loading ? <Spinner size="sm" margin={0} /> : <Checkbox isChecked={eventCompetition.exportBallots} margin={0} onChange={(e) => onToggleCompetition(eventCompetition.id, e.target.checked)} />}
          <HStack padding={0} margin={0} justifyContent="flex-start">
            {round.id === selectedRoundId ? (
              <Text margin={0} padding={0}>{label}</Text>
            ) : (
              <Link
                onClick={() => setNavigationDirection(true)}
                to={buildLink(eventId, eventCompetition.id, round.id)}
                title={label}
              />
            )}
            {completed && <Badge colorScheme="green">completed</Badge>}
          </HStack>
          <Text color="gray.500" style={{ marginInlineStart: 'auto' }}>
            {approvedBallots.length}
            /
            {allBallots.length}
          </Text>
        </HStack>
      </ListItem>
    );
  });
};

const BallotReviewContent = ({ prevCompetitionLink, nextCompetitionLink }) => {
  const { navigationDirection, setNavigationDirection } = useContext(NavigationDirectionContext);
  const history = useHistory();
  const toast = useToast();
  const {
    eventId,
    eventCompetitionId,
    roundId,
    memberId,
  } = useParams();
  const {
    data,
    loading: eventCompLoading,
  } = useQuery(EVENT_COMPETITION_WITH_SCORE_DETAILS, {
    fetchPolicy: 'network-first',
    variables: { id: eventCompetitionId },
  });

  const eventCompetition = data?.fetchEventCompetitionDetail;

  if (!eventCompetitionId || eventCompLoading) {
    return <CenteredMessage message="Loading..." />;
  }
  if (!eventCompetition) {
    return <CenteredMessage message="An error occured. Please try again later." />;
  }
  if (!eventCompetition.exportBallots) {
    return <CenteredMessage message={`${eventCompetition.title} ballots are not being exported`} />;
  }

  const roundIdx = eventCompetition.rounds.findIndex((r) => r.id === roundId);
  const round = eventCompetition.rounds[roundIdx];
  const label = eventCompetition.rounds.length > 1 ? round.name : eventCompetition.title;

  if (!memberId && judgablesForRound(round).length > 0) {
    const member = navigationDirection ? getFirstMember(round) : getLastMember(round);
    return <Redirect to={buildLink(eventId, eventCompetition.id, round.id, member.id)} />;
  }

  const allMembers = getAllMembers(round);
  const currentMemberIdx = allMembers.findIndex((member) => member.id === memberId);
  const currentMember = allMembers[currentMemberIdx];

  let prevStudentLink;
  if (currentMemberIdx - 1 >= 0) {
    const prevMemberId = allMembers[currentMemberIdx - 1].id;
    prevStudentLink = buildLink(eventId, eventCompetition.id, round.id, prevMemberId);
  } else if (roundIdx - 1 >= 0) {
    const prevRoundId = eventCompetition.rounds[roundIdx - 1].id;
    prevStudentLink = buildLink(eventId, eventCompetition.id, prevRoundId);
  } else {
    prevStudentLink = prevCompetitionLink;
  }
  const hasPrevStudent = prevStudentLink !== null;

  let nextStudentLink;
  if (currentMemberIdx + 1 < allMembers.length) {
    const nextMemberId = allMembers[currentMemberIdx + 1].id;
    nextStudentLink = buildLink(eventId, eventCompetition.id, round.id, nextMemberId);
  } else if (roundIdx + 1 < eventCompetition.rounds.length) {
    const nextRoundId = eventCompetition.rounds[roundIdx + 1].id;
    nextStudentLink = buildLink(eventId, eventCompetition.id, nextRoundId);
  } else {
    nextStudentLink = nextCompetitionLink;
  }
  const hasNextStudent = nextStudentLink !== null;

  const goToPrevStudent = () => {
    setNavigationDirection(false);
    history.push(prevStudentLink);
  };

  const goToNextStudent = () => {
    if (!hasNextStudent) {
      toast({
        title: 'All ballots completed!',
        status: 'success',
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setNavigationDirection(true);
    history.push(nextStudentLink);
  };

  return (
    <Box width="100%" borderInlineStart="2px solid #E3E8F0">
      <Flex width="100%" alignItems="center" justifyContent="space-around" paddingBlock={6}>
        <Heading as="h1" size="md">
          <Text color="#3E6796" display="inline">COMPETITION: </Text>
          {label}
        </Heading>
      </Flex>
      <VStack backgroundColor="#F2F2F7" height="100%" paddingBlockEnd={4} paddingInline={2}>
        {allMembers.length > 0 && (
          <HStack justifyContent="flex-start" width="100%" padding={4}>
            <Text color="#3E6796" fontWeight="bold">STUDENT ID: </Text>
            <MemberSelect
              members={allMembers}
              selectedMemberId={currentMember?.id}
              onSelect={(newMemberId) => {
                history.push(buildLink(eventId, eventCompetition.id, round.id, newMemberId));
              }}
            />
            <IconButton
              icon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              isDisabled={!hasPrevStudent}
              onClick={goToPrevStudent}
            />
            <IconButton
              icon={<FaArrowRight />}
              variant="ghost"
              size="sm"
              isDisabled={!hasNextStudent}
              onClick={goToNextStudent}
            />
          </HStack>
        )}
        {judgablesForRound(round).length === 0 ? (
          <Text>No ballots available for competition</Text>
        ) : (
          <MemberBallots
            key={currentMember.id}
            eventCompetitionId={eventCompetitionId}
            member={currentMember}
            onSubmitComplete={goToNextStudent}
          />
        )}
      </VStack>
    </Box>
  );
};

const MemberSelect = ({ members, selectedMemberId, onSelect }) => {
  return (
    <Select
      onChange={(e) => {
        onSelect(e.target.value);
      }}
      value={selectedMemberId}
      padding={1}
      borderRadius={4}
      backgroundColor="white"
      width="fit-content"
    >
      {members.map((member) => (
        <option key={member.id} value={member.id}>
          {member.code}
        </option>
      ))}
    </Select>
  );
};

const MemberBallots = ({
  eventCompetitionId,
  member,
  onSubmitComplete,
}) => {
  const [ballotsToUpdate, setBallotsToUpdate] = useState(
    member.ballotScores.reduce((acc, bs) => ({
      ...acc,
      [bs.id]: bs.comments,
    }), {}),
  );
  const [submitBallots, { loading }] = useMutation(EDIT_BALLOT_SCORES);

  const onSubmit = async (approved) => {
    const ballotsToUpdatePayload = Object.entries(ballotsToUpdate).map(([ballotId, comments]) => ({
      id: ballotId,
      comments,
      approved,
    }));

    await submitBallots({
      variables: {
        input: {
          eventCompetitionId,
          ballotScores: ballotsToUpdatePayload,
        },
      },
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
    onSubmitComplete();
  };

  return (
    <VStack width="100%" margin={0} padding={0}>
      <MemberAttachments attachments={member.attachments} />
      <Box width="100%" style={{ marginTop: 0 }} margin={0} padding={0}>
        {member.ballotScores?.length > 0 ? (
          <Box padding={0} margin={0} width="100%">
            {member.ballotScores.map((bs) => (
              <MemberBallot
                key={bs.id}
                ballot={bs}
                comments={ballotsToUpdate[bs.id]}
                onChange={(newComments) => {
                  setBallotsToUpdate({
                    ...ballotsToUpdate,
                    [bs.id]: newComments,
                  });
                }}
              />
            ))}
          </Box>
         ) : (
            <Box padding={4}>
              <Text>{member.code} is marked as present but has no ballots!</Text>
            </Box>
          )}
      </Box>
      <HStack justifyContent="flex-end" width="100%" paddingInline={4}>
        <HStack>
          <Button
            variant="outline"
            colorScheme="purple"
            isLoading={loading}
            onClick={() => onSubmit(false)}
          >
            Come Back Later
          </Button>
          <Button
            variant="solid"
            colorScheme="green"
            isLoading={loading}
            onClick={() => onSubmit(true)}
          >
            Approve
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

const MemberAttachments = ({ attachments }) => {
  if (!attachments) {
    return null;
  }

  return (
    <Box>
      {attachments.map((attachment) => <Attachment key={attachment.id} attachment={attachment} />)}
    </Box>
  );
};

const Attachment = ({ attachment }) => {
  const submissionTime = `(submitted at ${stringifyDate(attachment.updatedAt)} ${USER_SHORT_TZ})`;

  return (
    <VStack key={attachment.id} gap={0}>
      <Link
        href={attachment.attachmentUrl}
        title={attachment.attachmentName}
      />
      <Text>{submissionTime}</Text>
    </VStack>
  );
};

const MemberBallot = ({ ballot, comments, onChange }) => {
  const [showScores, setShowScores] = useState(false);
  const { judge, sections, status, privateNotes } = ballot;
  const judgeName = `${judge.fetchUser.firstname} ${judge.fetchUser.lastname}`;

  return (
    <VStack
      paddingTop={0}
      paddingInline={4}
      paddingBottom={6}
      width="100%"
      alignItems="flex-start"
    >
      <HStack justifyContent="flex-start" gap={4}>
        <Text>
          {judgeName}
        </Text>
        {status === 'approved' && (
          <Text color="green">
            <Icon>
              <FaCheck />
            </Icon>
            Approved
          </Text>
        )}
      </HStack>
      <VStack
        width="100%"
        padding={4}
        border="1px solid #DDDDDD"
        borderRadius={1}
        backgroundColor="white"
        alignItems="flex-start"
      >
        <HStack>
          <IconButton
            icon={showScores ? <FaChevronDown /> : <FaChevronRight />}
            variant="ghost"
            size="sm"
            onClick={() => setShowScores(!showScores)}
          />
          <Text size="md">Scores</Text>
        </HStack>
        {showScores && (
          <Box
            width="100%"
            borderColor="gray.300"
            borderTopWidth="1px"
            borderBottomWidth="1px"
            paddingBlock={2}
          >
            {sections.map((section) => (
              <HStack key={section.id} width="100%" justifyContent="space-between" alignItems="center" margin={0} padding={0}>
                <Text margin={0}>{section.ballotSection.title}:</Text>
                <HStack gap={0}>
                  <Text margin={0}>{section.points}</Text>
                  <Text margin={0}>{"/"}</Text>
                  <Text margin={0}>{section.ballotSection.totalPoints}</Text>
                </HStack>
              </HStack>
            ))}
          </Box>
        )}
        <Box width="100%">
          <Text size="md">Comments</Text>
          <textarea
            style={{
              width: '100%',
              border: '1px solid #888888',
              paddingBlock: '4px',
              paddingInline: '8px',
              minHeight: '48px',
              fieldSizing: 'content',
            }}
            value={comments}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        </Box>
        <Text>Private comments: {privateNotes}</Text>
      </VStack>
    </VStack>
  );
};

const CenteredMessage = ({ message }) => (
  <Flex height="100px" width="100%" alignItems="center" justifyContent="center">
    <Text>
      {message}
    </Text>
  </Flex>
);
CenteredMessage.propTypes = {
  message: PropTypes.string.isRequired,
};
